import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import { gePutPreAssignedUrlRes, getCampaignTemplateRes, getPreAssignedUrlRes, getTemplateFromUrl, saveCampaignTemplateRes, saveTestTemplateRes, templateKeys, updateCampaignTemplateRes, uploadCustomDocumentRes, useDeleteTemplate } from "core";
import TextEditorView from "./text-editor/View";
import { XCircle, Loader2, ArrowLeft, Save, FlaskConical, Eye, Tag, FolderOpen, Sparkles, AlertCircle, Trash2 } from "lucide-react";
import { Input } from "components/ui/input";
import { ImageHandlerService } from "core";
import { SkeletonSwitch } from "skeleton";
import { TemplateEditorSkeleton } from "skeleton/template/TemplateSkeleton";
import { toast } from "components/ui/toast";
import { useQueryClient } from "@tanstack/react-query";


type EditorHandle = {
    getContent: () => string;
    content?: string;
};

const _Editor = React.forwardRef<EditorHandle, any>((_props, ref) => {
    const [local, setLocal] = useState("<div/>");
    React.useImperativeHandle(ref, () => ({
        getContent: () => local,
        content: local,
    }));
    return (
        <textarea
            style={{ width: "100%", minHeight: 300 }}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            placeholder="Editor HTML here"
        />
    );
});

type EmailEditorHandle = {
    exportHtml: () => Promise<string>;
};
const EmailEditor = React.forwardRef<EmailEditorHandle, any>((_props, ref) => {
    const [val, setVal] = useState("<div/>");
    React.useImperativeHandle(ref, () => ({
        exportHtml: async () => val,
    }));
    return (
        <textarea
            style={{ width: "100%", minHeight: 300 }}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="EmailEditor HTML here"
        />
    );
});

type ViewType = "editor" | "preview";
function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

export default function TemplateEditor() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ templateId?: string }>();
    const query = useQuery();

    const [initialLoading, setInitialLoading] = useState<boolean>(true)
    const [saveLoading, setSaveLoading] = useState<boolean>(false)
    const [tempName, setTempName] = useState<string>("");
    const [catName, setCatName] = useState<string>("");
    const [loadEditor, setLoadEditor] = useState<boolean>(false);
    const [paramValue, setParamValue] = useState<number | null>(null);
    const [viewType, setViewType] = useState<ViewType>("editor");
    const [content, setContent] = useState<string>("");
    const [viewContent, setViewContent] = useState<string>("");
    const [editTempName, setEditTempName] = useState<boolean | null>(null);
    const [editCatName, setEditCatName] = useState<boolean | null>(null);
    const [actionType, setActionType] = useState<"Create" | "Update">("Create");
    const [templateId, setTemplateId] = useState<string | number | undefined>(undefined);
    const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    // TemplateEditor always uses campaign-storage (legacy templates)
    const deleteTemplateMutation = useDeleteTemplate();

    const printMeRef = useRef<HTMLDivElement | null>(null);
    const editorRef = useRef<EditorHandle | null>(null);
    const emailEditorRef = useRef<EmailEditorHandle | null>(null);

    const checkFormValidation = useCallback(
        (key: "temp" | "cat") => {
            if (key === "temp") setEditTempName(!tempName);
            if (key === "cat") setEditCatName(!catName);
        },
        [tempName, catName],
    );

    const finalFormValidation = useCallback((): boolean => {
        const ok = Boolean(tempName && catName);
        if (!ok) {
            checkFormValidation("temp");
            checkFormValidation("cat");
        }
        return ok;
    }, [tempName, catName, checkFormValidation]);

    // ===== navigation/back =====
    const backNavigate = useCallback(() => {
        navigate({ pathname: "/templates" }); // adjust route if needed
        // If using store: clear TemplateName
    }, [navigate]);

    const _getHeight = useCallback(() => {
        return printMeRef.current?.offsetHeight ?? 0;
    }, []);

    const dataURLtoFile = useCallback((dataUrl: string, filename: string) => {
        const arr = dataUrl.split(",");
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : "image/png";
        const bstr = atob(arr[1] || "");
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], filename, { type: mime });
    }, []);

    const getAllUrls = useCallback((text: string) => {
        const urlRegex = /(https?:\/\/[^\s]+(\s?\w+)+(.(?:png|jpg|jpeg))+)/g;
        return text.match(urlRegex) || [];
    }, []);

    const _getUpdatedContent = useCallback(async (freshContent?: any) => {
        const urls = getAllUrls(viewContent || freshContent || content);
        const downloadedImages = await ImageHandlerService.downloadUrls(urls);
        let updated = viewContent || freshContent || content;
        for (const view of downloadedImages) {
            updated = updated.replaceAll(view.url, view.base64);
        }
        setViewContent(updated);
    }, [content, viewContent, getAllUrls]);

    const uploadCustomDocument = useCallback(
        (objectFields: Record<string, string>, file: File | string, url: string): Promise<number> => {
            return new Promise((resolve, reject) => {
                try {
                    const formData = new FormData();
                    formData.append("key", objectFields["key"]);
                    formData.append("AWSAccessKeyId", objectFields["AWSAccessKeyId"]);
                    formData.append("x-amz-security-token", objectFields["x-amz-security-token"]);
                    formData.append("policy", objectFields["policy"]);
                    formData.append("signature", objectFields["signature"]);
                    formData.append("file", file);
                    uploadCustomDocumentRes(url, formData).then((res) => resolve(res.status));
                } catch (err) {
                    reject(err);
                }
            });
        },
        []);

    const navigateTo = useCallback(() => {
        navigate({ pathname: "/templates" });
    }, [navigate]);

    const getAllTemplateUrl = useCallback(async (bucketId: string | number) => {
        try {
            const res = await getCampaignTemplateRes(bucketId);
            return res.data.data as string;
        } catch (e) {
            throw e;
        }
    }, []);

    const toggleView = useCallback(async () => {
        setViewType("preview");
        if (paramValue !== 2) {
            return
        } else {
            const html = await emailEditorRef.current?.exportHtml();
            setContent(html || "");
        }
    }, [paramValue]);

    const editorView = useCallback(() => {
        setViewType("editor");
    }, []);

    const saveContent = useCallback(async () => {
        if (!tempName || !catName) return false;

        let body: any = {
            data: content,
            name: tempName,
        };
        if (!body.name) return false;

        toast({ title: actionType === 'Update' ? 'Updating Template' : 'Creating Template' });
        setSaveLoading(true);

        // ── Thumbnail: render HTML into hidden off-screen element ──────────
        // No view switch needed. Works whether we're in editor OR preview mode.
        let thumbnailDataURL = '';
        if (content.trim()) {
            let tempDiv: HTMLDivElement | null = null;
            try {
                tempDiv = document.createElement('div');
                tempDiv.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:600px;pointer-events:none;opacity:0;z-index:-1;';
                tempDiv.innerHTML = content;
                document.body.appendChild(tempDiv);
                await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
                const canvas = await html2canvas(tempDiv, { scale: 1, useCORS: true });
                thumbnailDataURL = canvas.toDataURL('image/png');
            } catch (err) {
                console.warn('Thumbnail capture skipped:', err);
            } finally {
                tempDiv?.remove();
            }
        }

        let status: number | undefined;
        let preAssignedData: any;

        if (!templateId) {
            preAssignedData = await getPreAssignedUrl(body.name, null);
            const fileName = `${body.name}.png`;
            const file = dataURLtoFile(thumbnailDataURL || "data:image/png;base64,", fileName);
            status = await uploadCustomDocument(preAssignedData.data.thumbnailFields, file, preAssignedData.data.objectUrl);
            status = await uploadCustomDocument(preAssignedData.data.objectFields, content, preAssignedData.data.thumbnailUrl);
            body["template_key"] = preAssignedData?.data?.objectFields?.key;
            body["post_status"] = status;
            body["file_secret"] = preAssignedData?.data?.file_secret;
            body["tag_name"] = catName;
            delete body.data;
            try {
                await saveCampaignTemplateRes(body);
                toast({ title: "Template created successfully!" });
                await queryClient.invalidateQueries({ queryKey: templateKeys.userList() });
                await queryClient.refetchQueries({ queryKey: templateKeys.userList() });
                navigateTo();
            } catch {
                toast.error({ title: "Template name already exist" });
            } finally {
                setSaveLoading(false);
            }
        } else {
            preAssignedData = await gePutPreAssignedUrlRes(templateId, null);

            const fileName = `${body.name}.png`;
            const file = dataURLtoFile(thumbnailDataURL || "data:image/png;base64,", fileName);

            status = await uploadCustomDocument(preAssignedData.data.thumbnailFields, file, preAssignedData.data.objectUrl);
            status = await uploadCustomDocument(preAssignedData.data.objectFields, content, preAssignedData.data.thumbnailUrl);

            body["template_key"] = preAssignedData?.data?.objectFields?.key;
            body["post_status"] = status;
            body["file_secret"] = preAssignedData?.data?.file_secret;
            body["template_id"] = templateId;
            body["new_name"] = tempName;
            body["tag_name"] = catName;

            delete body.name;
            delete body.data;

            try {
                await updateCampaignTemplateRes(body);
                toast({ title: "Template updated successfully!" });
                await queryClient.invalidateQueries({ queryKey: templateKeys.userList() });
                await queryClient.refetchQueries({ queryKey: templateKeys.userList() });
                navigateTo();
            } catch {
                toast.error({ title: "Template name already exist" });
            } finally {
                setSaveLoading(false);
            }
        }
    }, [
        tempName,
        catName,
        content,
        actionType,
        templateId,
        dataURLtoFile,
        navigateTo,
        setSaveLoading,
        uploadCustomDocument,
    ]);

    async function getPreAssignedUrl(name: string, thumb_url: any) {
        return new Promise((resolve, reject) => {
            try {
                getPreAssignedUrlRes(name, thumb_url).then((res) => {
                    resolve(res);
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    const saveValue = useCallback(async () => {
        // Stay in editor mode during save — thumbnail captured off-screen.
        // Switching to preview was freezing the entire UI for 5-7 seconds.
        await saveContent();
    }, [saveContent]);

    const handleDeleteTemplate = useCallback(async () => {
        if (!templateId) return;
        setDeleteLoading(true);
        try {
            // TemplateEditor only handles legacy campaign-storage templates
            await deleteTemplateMutation.mutateAsync(templateId);
            toast.success("Template moved to trash");
            navigateTo();
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || "Failed to move template to trash";
            // 424 = template used in active campaign
            toast.error(msg);
        } finally {
            setDeleteLoading(false);
            setShowDeleteConfirm(false);
        }
    }, [templateId, deleteTemplateMutation, navigateTo]);

    const handleOnSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            const ok = finalFormValidation();
            if (!ok) return;

            if (paramValue === 2) {
                const html = await emailEditorRef.current?.exportHtml();
                setContent(html || "");
            }

            setIsFormSubmitted(true);
            if ((!tempName && !catName) || !content.trim()) {
                return false;
            }

            await saveValue();
            return true;
        },
        [finalFormValidation, paramValue, tempName, catName, saveValue],
    );

    const testTemplate = useCallback(async () => {
        const ok = finalFormValidation();
        if (!ok) return;

        if (paramValue === 2) {
            const html = await emailEditorRef.current?.exportHtml();
            setContent(html || "");
        } else {
            // setContent(editorRef.current?.getContent() || "");
        }

        if ((!tempName && !catName) || !content) {
            return false;
        }

        try {
            await saveTestTemplateRes(content);
            toast.success("Check your mail, test template has been sent");
        } catch {
            toast.error("Error in sending test template.");
        } finally {
            // setLoading(false);
        }
    }, [finalFormValidation, paramValue, tempName, catName, content]);

    const getTemplateById = useCallback(async (bucketId?: string | number) => {
        if (!bucketId) return;
        try {
            const res = await getCampaignTemplateRes(bucketId);
            setTempName(res.data.name || "");
            setCatName(res.data.tag || "");
            if (res?.data?.data) {
                const t = await getTemplateFromUrl(res.data.data);
                setContent(t.data || "");
            }
        } catch {
            toast.error("Something went wrong!");
        } finally {
        }
    }, []);

    const getTemplateByUrl = async (url: string) => {
        const template = await getTemplateFromUrl(url);
        setContent(template.data ?? "");
    };

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            setActionType("Create");


            // 2) from route: /:templateId?
            const pTemplateId = params.templateId;
            const qContent = (location.state as { content?: string } | null)?.content;;
            const qTemplateIds = query.get("templateIds");

            // Case A: /new?content=<url>
            if (pTemplateId === "new" && qContent) {
                if (!cancelled) setLoadEditor(true);
                try {
                    const fileResponse = await getTemplateFromUrl(qContent);
                    if (!cancelled) setContent(fileResponse.data ?? "");
                    if (initialLoading) setInitialLoading(false);
                    return;
                } catch (e) {
                    console.error("getTemplateFromUrl failed:", e);
                    if (initialLoading) setInitialLoading(false);
                    return;
                }
            }

            if (qTemplateIds) {     /* Not Worked Maybe Need to Remove THAT */
                if (!cancelled) setLoadEditor(true);
                const ids = qTemplateIds.split(",").map((s) => s.trim());
                try {
                    const first = ids[0];
                    if (/^https?:\/\//i.test(first)) {
                        await getTemplateByUrl(first);
                    } else {
                        const url = await getAllTemplateUrl(first);
                        if (url) await getTemplateByUrl(url);
                    }
                } catch (e) {
                    console.error("Failed loading templateIds:", e);
                }
            }

            // Case C: numeric :templateId -> fetch by id
            if (
                pTemplateId &&
                pTemplateId !== "new" &&
                !Number.isNaN(Number(pTemplateId))
            ) {
                if (!cancelled) {
                    setTemplateId(pTemplateId);
                    setActionType("Update");
                    setLoadEditor(true);
                }
                await getTemplateById(pTemplateId);
                if (initialLoading) setInitialLoading(false);
                return;
            } else if (pTemplateId && pTemplateId !== "new") {
                // If templateId is actually "content" string payload (as in Vue else-if)
                if (!cancelled) {
                    setContent(pTemplateId);
                    setLoadEditor(true);
                }
                if (initialLoading) setInitialLoading(false);
                return;
            }

            if (initialLoading) setInitialLoading(false);

            // Default: just load editor
            if (!cancelled) {
                setLoadEditor(true);
            }
        };
        init();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.templateId]);


    useEffect(() => {
        const t = query.get("type");
        setParamValue(t === "draganddrop" ? 2 : 1);
    }, [query]);


    useEffect(() => {
        if (content) console.log("content in here -->", content)
    }, [content])



    // ===== UI helpers =====
    const templateNameInvalid = isFormSubmitted && !tempName;
    const templateCatInvalid = isFormSubmitted && !catName;

    const stripHtml = (html: string) => html.replace(/<[^>]*>?/gm, "");
    const count = stripHtml(content || "").length;
    const maxCharCount = 50_000;  // realistic for email HTML templates
    const pct = maxCharCount > 0 ? Math.min((count / maxCharCount) * 100, 100) : 0;
    const charPercentage = Math.round(pct);
    const isTyping = count > 0;

    /* ── shared input class ── */
    const fieldCls = (err?: boolean | null) =>
        `h-9 pl-9 pr-3 rounded-lg text-[13px] font-medium
         bg-surface dark:bg-dark-700
         border ${err ? "border-red-400 dark:border-red-500" : "border-line dark:border-dark-600"}
         text-content dark:text-gray-100 placeholder:text-content-subtle dark:placeholder:text-content-muted
         focus:border-blue-400 focus:ring-2 focus:ring-blue-400/15
         transition-all shadow-sm`;

    return (
        <div className="w-full h-full flex flex-col
            px-0 pt-0 pb-0
            sm:px-4 sm:pt-4 sm:pb-4
            lg:px-8 overflow-hidden">
            <SkeletonSwitch isLoading={initialLoading} skeleton={<TemplateEditorSkeleton />}>
                {/* ── Premium Card Shell ── */}
                <div className="bg-surface dark:bg-dark-800 border-0 sm:border border-line dark:border-dark-700
                    rounded-none sm:rounded-2xl overflow-hidden flex flex-col h-full"
                    style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)" }}>

                    {/* 3-px accent bar */}
                    <div className="h-[3px] w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 flex-shrink-0" />

                    {/* ── ROW 1: Back + Brand + Actions ── */}
                    <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 h-12 sm:h-14
                        border-b border-line-subtle dark:border-dark-700 flex-shrink-0">

                        {/* ← Back */}
                        <button
                            type="button"
                            onClick={backNavigate}
                            disabled={saveLoading}
                            className="inline-flex items-center gap-1 h-8 px-2 sm:px-2.5 rounded-lg flex-shrink-0
                                text-[12px] font-medium text-content-muted dark:text-gray-400
                                hover:text-gray-800 dark:hover:text-gray-200
                                hover:bg-gray-100 dark:hover:bg-dark-700
                                active:scale-[0.97] transition-all duration-150 disabled:opacity-50"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Back</span>
                        </button>

                        <div className="h-5 w-px bg-gray-200 dark:bg-dark-600 flex-shrink-0" />

                        {/* Brand mark */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600
                                flex items-center justify-center shadow-sm shadow-blue-500/25 flex-shrink-0">
                                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-meta sm:text-ui font-bold text-content dark:text-white leading-tight truncate">
                                    {actionType === "Create" ? "Create Template" : "Edit Template"}
                                </p>
                            </div>
                        </div>

                        {/* Character progress */}
                        {isTyping && (
                            <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
                                <div className="w-16 h-1 bg-gray-200 dark:bg-dark-600 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${charPercentage >= 80 ? "bg-amber-500" : "bg-blue-500"}`}
                                        style={{ width: `${charPercentage}%` }}
                                    />
                                </div>
                                <span className="text-[10px] font-semibold text-content-subtle dark:text-gray-500 tabular-nums">
                                    {charPercentage}%
                                </span>
                            </div>
                        )}

                        {/* ── Right Actions: Trash (edit mode) + Test + Create ── */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Move to Trash — only when editing an existing template */}
                            {actionType === "Update" && templateId && (
                                <button
                                    type="button"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={saveLoading || deleteLoading}
                                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg
                                        text-[12px] font-semibold
                                        text-red-500 dark:text-red-400
                                        bg-red-50 dark:bg-red-900/15
                                        border border-red-200 dark:border-red-800/40
                                        hover:bg-red-100 dark:hover:bg-red-900/30
                                        active:scale-[0.97] transition-all duration-150 disabled:opacity-50"
                                    title="Move to trash"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">Trash</span>
                                </button>
                            )}

                            {/* Test — subtle outlined */}
                            <button
                                type="button"
                                onClick={testTemplate}
                                disabled={saveLoading}
                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg
                                    text-[12px] font-semibold
                                    text-blue-600 dark:text-blue-400
                                    bg-blue-50 dark:bg-blue-900/20
                                    border border-blue-200 dark:border-blue-700/50
                                    hover:bg-blue-100 dark:hover:bg-blue-900/30
                                    active:scale-[0.97] transition-all duration-150 disabled:opacity-50"
                            >
                                <FlaskConical className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Test</span>
                            </button>

                            {/* Create / Update — primary gradient */}
                            <button
                                type="button"
                                onClick={handleOnSubmit as any}
                                disabled={saveLoading}
                                className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg
                                    text-[12px] font-bold text-white
                                    bg-gradient-to-r from-blue-600 to-indigo-600
                                    hover:from-blue-700 hover:to-indigo-700
                                    shadow-md shadow-blue-500/25
                                    hover:shadow-lg hover:shadow-blue-500/30
                                    active:scale-[0.97] transition-all duration-150
                                    disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {saveLoading ? (
                                    <><Loader2 className="w-3.5 h-3.5 animate-spin" />{actionType === "Create" ? "Creating…" : "Updating…"}</>
                                ) : (
                                    <><Save className="w-3.5 h-3.5" />{actionType === "Create" ? "Create" : "Update"}</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* ── ROW 2: Form inputs ── */}
                    <form onSubmit={handleOnSubmit}>
                        <div className="flex flex-col sm:flex-row items-start gap-0 px-3 sm:px-4 py-2 sm:py-2.5 flex-shrink-0
                            bg-gray-50/80 dark:bg-dark-900/30
                            border-b border-line-subtle dark:border-dark-700/60">
                            {/* Template Name */}
                            <div className="w-full sm:flex-1 sm:min-w-0 sm:pr-3 pb-1.5 sm:pb-0">
                                <div className="relative">
                                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-indigo-300 dark:text-indigo-700 pointer-events-none" />
                                    <Input
                                        id="template-name"
                                        placeholder="Template name…"
                                        value={tempName}
                                        onChange={(e) => setTempName(e.target.value)}
                                        onInput={() => checkFormValidation("temp")}
                                        aria-invalid={templateNameInvalid || editTempName || undefined}
                                        disabled={saveLoading}
                                        className={fieldCls(templateNameInvalid || editTempName)}
                                    />
                                </div>
                                {(templateNameInvalid || editTempName) && (
                                    <p className="flex items-center gap-1 mt-1 text-[11px] text-red-500 dark:text-red-400 font-medium">
                                        <AlertCircle className="w-3 h-3 flex-shrink-0" /> Template name is required
                                    </p>
                                )}
                            </div>

                            {/* Vertical separator — desktop only */}
                            <div className="hidden sm:flex flex-shrink-0 self-stretch items-center">
                                <div className="w-px h-5 bg-indigo-100 dark:bg-dark-600" />
                            </div>

                            {/* Template Category */}
                            <div className="w-full sm:flex-1 sm:min-w-0 sm:pl-3">
                                <div className="relative">
                                    <FolderOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-indigo-300 dark:text-indigo-700 pointer-events-none" />
                                    <Input
                                        id="template-category"
                                        placeholder="Category…"
                                        value={catName}
                                        onChange={(e) => setCatName(e.target.value)}
                                        onInput={() => checkFormValidation("cat")}
                                        aria-invalid={templateCatInvalid || editCatName || undefined}
                                        disabled={saveLoading}
                                        className={fieldCls(templateCatInvalid || editCatName)}
                                    />
                                </div>
                                {(templateCatInvalid || editCatName) && (
                                    <p className="flex items-center gap-1 mt-1 text-[11px] text-red-500 dark:text-red-400 font-medium">
                                        <AlertCircle className="w-3 h-3 flex-shrink-0" /> Category is required
                                    </p>
                                )}
                            </div>
                        </div>
                    </form>

                    {/* ── Editor area ── */}
                    <div className={`relative overflow-hidden flex-1 min-h-0 ${viewType === "preview" ? "hidden" : ""}`}>
                        {paramValue === 2 ? (
                            <EmailEditor ref={emailEditorRef} />
                        ) : (
                            <TextEditorView
                                ref={editorRef}
                                editorView={editorView}
                                toggleView={toggleView}
                                viewType={viewType}
                                loadEditor={loadEditor}
                                content={content}
                                setContent={setContent}
                                templateId={templateId}
                            />
                        )}
                    </div>

                    {/* ── Preview pane ── */}
                    {viewType === "preview" && (
                        <div className="flex flex-col">
                            {/* Preview header */}
                            <div className="flex items-center gap-3 px-5 py-3 border-b border-line dark:border-dark-700 bg-gray-50/80 dark:bg-dark-900/30 flex-shrink-0">
                                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm flex-shrink-0">
                                    <Eye className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-ui-lg font-bold text-content dark:text-white leading-tight">Preview</p>
                                    <p className="text-[11px] text-content-subtle dark:text-gray-500">How your email will look</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button type="button" onClick={handleOnSubmit as any} disabled={saveLoading}
                                        className="h-8 px-4 rounded-xl text-[12px] font-bold text-white
                                            bg-gradient-to-r from-blue-600 to-indigo-600
                                            hover:from-blue-700 hover:to-indigo-700
                                            shadow-sm transition-all disabled:opacity-60 flex items-center gap-1.5">
                                        <Save className="w-3.5 h-3.5" />
                                        {saveLoading
                                            ? (actionType === "Create" ? "Creating…" : "Updating…")
                                            : (actionType === "Create" ? "Create Template" : "Update Template")}
                                    </button>
                                    <button type="button" onClick={editorView}
                                        className="h-8 w-8 rounded-xl flex items-center justify-center
                                            text-content-subtle dark:text-gray-500
                                            hover:bg-gray-200 dark:hover:bg-dark-700
                                            hover:text-content-secondary dark:hover:text-gray-300
                                            transition-colors" aria-label="Close preview">
                                        <XCircle size={16} />
                                    </button>
                                </div>
                            </div>
                            {/* Preview content */}
                            {content && (
                                <div
                                    id="preview-content-area"
                                    ref={printMeRef}
                                    className="overflow-y-auto p-6 bg-gray-50 dark:bg-dark-900/20"
                                    style={{ maxHeight: "calc(100dvh - 220px)" }}
                                    dangerouslySetInnerHTML={{ __html: content }}
                                />
                            )}
                        </div>
                    )}
                </div>
            </SkeletonSwitch>

            {/* ── Delete Confirm Overlay ── */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-surface dark:bg-dark-800 rounded-2xl shadow-2xl border border-line dark:border-dark-600 p-6 max-w-sm w-full">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                                <Trash2 className="w-5 h-5 text-red-500 dark:text-red-400" />
                            </div>
                            <div>
                                <p className="text-[15px] font-bold text-content dark:text-white">Move to Trash?</p>
                                <p className="text-[12px] text-content-muted dark:text-gray-400 mt-0.5">You can restore it from Trash within 30 days.</p>
                            </div>
                        </div>
                        <p className="text-ui text-content-secondary dark:text-gray-300 mb-5 leading-relaxed">
                            <span className="font-semibold text-content dark:text-white">"{tempName}"</span>{" "}
                            will be moved to trash. Templates used in active campaigns cannot be trashed.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleteLoading}
                                className="flex-1 px-4 py-2 rounded-xl text-[13px] font-semibold
                                    bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300
                                    hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteTemplate}
                                disabled={deleteLoading}
                                className="flex-1 px-4 py-2 rounded-xl text-[13px] font-semibold
                                    bg-red-500 hover:bg-red-600 text-white transition-colors
                                    disabled:opacity-60 flex items-center justify-center gap-2"
                            >
                                {deleteLoading
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Trash2 className="w-3.5 h-3.5" />
                                }
                                Move to Trash
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .cke_contents { height: calc(100dvh - 480px) !important; }
                #preview-content-area img {
                    max-width: 100% !important; height: auto !important;
                    max-height: calc(100dvh - 300px) !important;
                    object-fit: contain !important;
                    display: block; margin-left: auto !important; margin-right: auto !important;
                }
            `}</style>
        </div>
    );
}
